import { useAppointment, useProfile } from '@hook'
import { FormButton, Modal } from '@ui'
import { getError, showToast } from '@util'
import moment from 'moment'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { BookAppointmentStepConfirm } from './components/BookAppointmentStepConfirm'
import { BookAppointmentStepDateTime } from './components/BookAppointmentStepDateTime'
import { BookAppointmentStepDetails } from './components/BookAppointmentStepDetails'
import { BookAppointmentStepPayment } from './components/BookAppointmentStepPayment'
import { BookAppointmentForm } from './components/BookAppointmentTypes'

type Props = {
  onClose: () => void
  isOpen?: boolean
}

const steps = [
  { id: 1, label: 'Details' },
  { id: 2, label: 'Date & Time' },
  { id: 3, label: 'Confirm Appointment' },
  { id: 4, label: 'Payment' }
] as const

const initialForm: BookAppointmentForm = {
  appointmentMode: 'physical',
  appointmentType: 'Consultation',
  date: null,
  doctor: '',
  facility: '',
  payment_option: '',
  reason: '',
  time: null,
  facilityName: '',
  doctorName: ''
}

const StepIndicator = ({ currentStep }: { currentStep: number }) => {
  return (
    <div className="mb-6 mt-1 flex items-center">
      {steps.map((step, index) => {
        const isActive = currentStep === step.id
        const isDone = currentStep > step.id

        return (
          <div className="flex flex-1 items-center" key={step.id}>
            <div className="flex items-center gap-2 flex-1">
              <span
                className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${
                  isActive || isDone ? 'bg-[#E8E8E8] text-[#1F2937]' : 'bg-[#F2F2F2] text-[#9AA0A6]'
                }`}
              >
                {step.id}
              </span>
              <span className={`text-xs flex-1 ${isActive ? 'font-semibold text-[#2B2B2B]' : 'text-[#8E8E8E]'}`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 ? <div className="mx-2 h-px min-w-[40px] bg-[#E9E9E9]" /> : null}
          </div>
        )
      })}
    </div>
  )
}

const BookAppointment = ({ onClose }: Props) => {
  const { profile } = useProfile()
  const [currentStep, setCurrentStep] = useState(1)
  const { bookAppointment } = useAppointment()
  const methods = useForm<BookAppointmentForm>({ defaultValues: initialForm, mode: 'onChange' })
  const { watch, formState } = methods
  const formData = watch()

  const canContinue =
    (currentStep === 1 && !!formData.facility && !!formData.doctor && !!formData.appointmentType && !!formData.date) ||
    (currentStep === 2 && !!formData.time?.startTime && !!formData.time?.endTime) ||
    currentStep === 3 ||
    (currentStep === 4 && !!formData.payment_option)

  const isFinalStep = currentStep === 4

  const nextLabel = currentStep === 3 ? 'Proceed' : 'Continue'

  const handleNext = async () => {
    if (!canContinue) return
    if (isFinalStep) {
      console.log(moment(formData.date).format('YYYY-MM-DD'))
      try {
        const payload = {
          data: {
            type: 'appointment',
            attributes: {
              appointment_at: moment(formData.date).format('YYYY-MM-DD'),
              purpose: formData.reason?.trim() || '',
              payment_option: formData.payment_option,
              appointment_mode: formData.appointmentMode.toLowerCase(),
              appointment_type: formData.appointmentType.toLowerCase(),
              start_time: formData.time?.startTime || '',
              end_time: formData.time?.endTime || ''
            },
            relationships: {
              patient: { data: { id: profile?.id, type: 'patient' } },
              staff: { data: { id: formData.doctor, type: 'staff' } },
              facility: { data: { id: formData.facility, type: 'facility' } }
            }
          }
        }
        await bookAppointment(payload)
        showToast('Appointment booked successfully')
        onClose()
      } catch (error) {
        getError(error)
      }
      return
    }
    setCurrentStep(prev => prev + 1)
  }

  const handleBack = () => {
    if (currentStep === 1) {
      onClose()
      return
    }
    setCurrentStep(prev => prev - 1)
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Book Appointment">
        <div className="flex max-h-[89vh] w-full max-w-[600px] flex-col bg-white overflow-auto">
          <div className="border-b border-[#EFEFEF] px-6 pt-5">
            <StepIndicator currentStep={currentStep} />
          </div>

          <div className="flex flex-col overflow-y-auto px-6 py-5">
            {currentStep === 1 ? <BookAppointmentStepDetails /> : null}
            {currentStep === 2 ? <BookAppointmentStepDateTime /> : null}
            {currentStep === 3 ? <BookAppointmentStepConfirm /> : null}
            {currentStep === 4 ? <BookAppointmentStepPayment /> : null}
          </div>

          <FormButton
            calcelRightIcon={true}
            cancel={currentStep === 1 ? 'Cancel' : 'Back'}
            cancleIcon={false}
            disabled={!canContinue || formState.isSubmitting}
            isLoading={formState.isSubmitting}
            onClick={handleNext}
            onClose={handleBack}
            submit={nextLabel}
            type="button"
          />
        </div>
      </Modal>
    </FormProvider>
  )
}

export default BookAppointment
